import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  INSTALLATION_CHECKLIST_SECTIONS,
  isInstallationChecklistSectionComplete,
  installationChecklistItemLabel,
  type InstallationChecklistPhotoCounts,
  type InstallationChecklistState,
} from '../data/installationChecklist';
// Display-only: photos uploaded before this checklist replaced the facility-readiness
// one are filed under those section ids, so the gallery can still caption them.
import { SALES_CHECKLIST_SECTIONS } from '../data/salesChecklist';
import type { ChecklistAttachmentApi, ChecklistAttachmentRow } from './SalesChecklistCards';
import FileDropZone from './FileDropZone';
import AttachmentVoidModal from './AttachmentVoidModal';
import { AttachmentVoidReason } from './AttachmentVoidNote';
import { useAttachmentVoid } from '../hooks/useAttachmentVoid';
import { isVoided } from '../api/attachmentVoid';
import './SalesChecklistCards.css';
import './InstallationChecklistCards.css';

interface InstallationChecklistCardsProps {
  state: InstallationChecklistState;
  onItemChange: (itemId: string, value: string | boolean) => void;
  disabled?: boolean;
  attachmentApi: ChecklistAttachmentApi;
  userId?: number | null;
  /** Lets the parent list unfinished items (photo progress only lives in here). */
  onPhotoCountsChange?: (counts: InstallationChecklistPhotoCounts) => void;
  /** When set, each section renders an "Add note" button that calls this with the section reference. */
  onAddSectionNote?: (section: { id: string; title: string }) => void;
}

/** Desktop file pickers often leave `type` empty; still allow common image extensions. */
function isLikelyImageFile(file: File): boolean {
  if (file.type && file.type.startsWith('image/')) return true;
  if (file.type) return false;
  return /\.(jpe?g|png|gif|webp|bmp|heic|heif|tif|tiff)$/i.test(file.name);
}

function attachmentCaption(sectionId: string): string {
  return (
    installationChecklistItemLabel(sectionId) ??
    SALES_CHECKLIST_SECTIONS.find((s) => s.id === sectionId)?.title ??
    sectionId
  );
}

const InstallationChecklistCards: React.FC<InstallationChecklistCardsProps> = ({
  state,
  onItemChange,
  disabled,
  attachmentApi,
  userId,
  onPhotoCountsChange,
  onAddSectionNote,
}) => {
  const [attachments, setAttachments] = useState<ChecklistAttachmentRow[]>([]);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [attachError, setAttachError] = useState('');
  const pendingItemRef = useRef<string | null>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const uploadInputRef = useRef<HTMLInputElement>(null);

  const loadAttachments = useCallback(async () => {
    try {
      const list = await attachmentApi.fetch();
      setAttachments(list);
      setAttachError('');
    } catch (err) {
      setAttachError(
        err instanceof Error ? err.message : 'Could not load checklist photos (check server and database migration).'
      );
    }
  }, [attachmentApi]);

  useEffect(() => {
    void loadAttachments();
  }, [loadAttachments]);

  const byItem = useMemo(() => {
    const map: Record<string, ChecklistAttachmentRow[]> = {};
    for (const row of attachments) {
      if (!map[row.section_id]) map[row.section_id] = [];
      map[row.section_id].push(row);
    }
    return map;
  }, [attachments]);

  // A voided photo does not satisfy a "take a picture" item, so it is not counted.
  const photoCounts = useMemo<InstallationChecklistPhotoCounts>(() => {
    const counts: InstallationChecklistPhotoCounts = {};
    for (const [sectionId, rows] of Object.entries(byItem)) {
      counts[sectionId] = rows.filter((r) => !isVoided(r)).length;
    }
    return counts;
  }, [byItem]);

  useEffect(() => {
    onPhotoCountsChange?.(photoCounts);
  }, [photoCounts, onPhotoCountsChange]);

  // Oldest first, with voided photos kept at the end of the gallery.
  const attachmentsSorted = useMemo(
    () =>
      [...attachments].sort((a, b) => {
        const voidDiff = Number(isVoided(a)) - Number(isVoided(b));
        if (voidDiff !== 0) return voidDiff;
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      }),
    [attachments]
  );

  const activePhotoCount = useMemo(() => attachments.filter((a) => !isVoided(a)).length, [attachments]);

  const uploadFilesForItem = useCallback(
    async (itemId: string, picked: File[]) => {
      if (picked.length === 0) return;
      setUploadBusy(true);
      setAttachError('');
      try {
        let uploaded = 0;
        for (const file of picked) {
          if (!isLikelyImageFile(file)) continue;
          await attachmentApi.upload(itemId, file, userId);
          uploaded += 1;
        }
        if (uploaded === 0) {
          setAttachError('No image files were uploaded. Try PNG or JPEG, or drag images here.');
          return;
        }
        await loadAttachments();
      } catch (err) {
        setAttachError(err instanceof Error ? err.message : 'Upload failed.');
      } finally {
        setUploadBusy(false);
      }
    },
    [attachmentApi, userId, loadAttachments]
  );

  const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const itemId = pendingItemRef.current;
    const picked = e.target.files ? Array.from(e.target.files) : [];
    e.target.value = '';
    pendingItemRef.current = null;
    if (!itemId || picked.length === 0) return;
    await uploadFilesForItem(itemId, picked);
  };

  const openPicker = (itemId: string, ref: React.RefObject<HTMLInputElement>) => {
    pendingItemRef.current = itemId;
    ref.current?.click();
  };

  const replaceAttachment = (row: ChecklistAttachmentRow) =>
    setAttachments((prev) => prev.map((r) => (r.id === row.id ? { ...r, ...row } : r)));

  const photoVoid = useAttachmentVoid<ChecklistAttachmentRow>({
    noun: 'photo',
    label: (a) => a.original_file_name,
    onVoid: async (a, reason) => {
      setAttachError('');
      replaceAttachment(await attachmentApi.void(a.id, reason, userId));
    },
    onUnvoid: async (a) => {
      setAttachError('');
      replaceAttachment(await attachmentApi.unvoid(a.id, userId));
    },
    onError: setAttachError,
  });

  return (
    <section className="sales-checklist-root sales-checklist-root--primary" aria-label="Installation checklist">
      {attachError ? (
        <p className="sales-checklist-attach-error" role="alert">
          {attachError}
        </p>
      ) : null}

      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="sales-checklist-file-input-hidden"
        aria-hidden="true"
        tabIndex={-1}
        onChange={handleFileInputChange}
      />
      <input
        ref={uploadInputRef}
        type="file"
        accept="image/*"
        multiple
        className="sales-checklist-file-input-hidden"
        aria-hidden="true"
        tabIndex={-1}
        onChange={handleFileInputChange}
      />

      <div className="sales-checklist-cards">
        {INSTALLATION_CHECKLIST_SECTIONS.map((section) => {
          const complete = isInstallationChecklistSectionComplete(section, state, photoCounts);

          return (
            <article
              key={section.id}
              className={`sales-checklist-card${complete ? ' sales-checklist-card--complete' : ''}`}
            >
              {complete && (
                <div className="sales-checklist-card-top">
                  <span className="sales-checklist-completed-badge">Completed</span>
                </div>
              )}
              <h4 className="sales-checklist-card-title">{section.title}</h4>
              {section.description ? (
                <p className="install-checklist-card-desc">{section.description}</p>
              ) : null}

              <ul className="sales-checklist-list">
                {section.items.map((item) => {
                  if (item.entry === 'field') {
                    return (
                      <li key={item.id} className="sales-checklist-item sales-checklist-item--line">
                        <label className="sales-checklist-field-label" htmlFor={`install-check-${item.id}`}>
                          {item.label}
                        </label>
                        <input
                          id={`install-check-${item.id}`}
                          type="text"
                          className="sales-checklist-input"
                          value={String(state[item.id] ?? '')}
                          disabled={disabled}
                          onChange={(e) => onItemChange(item.id, e.target.value)}
                          placeholder={item.placeholder ?? 'Enter details…'}
                        />
                      </li>
                    );
                  }

                  if (item.entry === 'photo') {
                    const rows = byItem[item.id] ?? [];
                    return (
                      <li key={item.id} className="sales-checklist-item install-checklist-item--photo">
                        <span className="sales-checklist-field-label">{item.label}</span>
                        {rows.length > 0 ? (
                          <ul className="sales-checklist-photo-thumbs">
                            {rows.map((a) => {
                              const src = attachmentApi.downloadUrl(a.id);
                              const voided = isVoided(a);
                              return (
                                <li
                                  key={a.id}
                                  className={`sales-checklist-photo-thumb${voided ? ' attachment-voided-thumb' : ''}`}
                                >
                                  <a
                                    href={src}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="sales-checklist-photo-thumb-link"
                                    aria-label={`Open photo ${a.original_file_name}`}
                                  >
                                    <img src={src} alt="" className="sales-checklist-photo-thumb-img" />
                                  </a>
                                  {!disabled && (
                                    <button
                                      type="button"
                                      className={voided ? 'attachment-unvoid-btn' : 'attachment-void-btn'}
                                      disabled={photoVoid.busy}
                                      onClick={() => (voided ? photoVoid.unvoid(a) : photoVoid.requestVoid(a))}
                                    >
                                      {voided ? 'Unvoid' : 'Void'}
                                    </button>
                                  )}
                                  <AttachmentVoidReason attachment={a} />
                                </li>
                              );
                            })}
                          </ul>
                        ) : (
                          <p className="install-checklist-photo-empty">No picture yet.</p>
                        )}

                        {!disabled && (
                          <div className="sales-checklist-photo-actions">
                            <div className="install-checklist-photo-btns">
                              <button
                                type="button"
                                className="sales-checklist-photo-btn"
                                disabled={uploadBusy}
                                onClick={() => openPicker(item.id, cameraInputRef)}
                              >
                                Take a picture
                              </button>
                              <button
                                type="button"
                                className="sales-checklist-photo-btn sales-checklist-photo-btn--secondary"
                                disabled={uploadBusy}
                                onClick={() => openPicker(item.id, uploadInputRef)}
                              >
                                Upload a picture
                              </button>
                            </div>
                            <FileDropZone
                              variant="compact"
                              className="sales-checklist-photo-drop"
                              accept="image/*"
                              multiple
                              disabled={uploadBusy}
                              prompt={<>Drop images here or <span className="file-drop-zone__browse-word">browse</span></>}
                              hint="PNG, JPEG, etc. — multiple files allowed."
                              onFiles={(files) => void uploadFilesForItem(item.id, files)}
                            />
                          </div>
                        )}
                      </li>
                    );
                  }

                  return (
                    <li key={item.id} className="sales-checklist-item sales-checklist-item--check">
                      <label className="sales-checklist-check-label" htmlFor={`install-check-${item.id}`}>
                        <input
                          id={`install-check-${item.id}`}
                          type="checkbox"
                          className="sales-checklist-checkbox"
                          checked={state[item.id] === true}
                          disabled={disabled}
                          onChange={(e) => onItemChange(item.id, e.target.checked)}
                        />
                        <span>{item.label}</span>
                      </label>
                    </li>
                  );
                })}
              </ul>

              {onAddSectionNote && (
                <div className="sales-checklist-note-actions">
                  <button
                    type="button"
                    className="sales-checklist-note-btn"
                    onClick={() => onAddSectionNote({ id: section.id, title: section.title })}
                  >
                    + Add note
                  </button>
                </div>
              )}
            </article>
          );
        })}
      </div>

      {attachmentsSorted.length > 0 && (
        <div className="sales-checklist-all-photos" aria-label="All checklist photos">
          <h4 className="sales-checklist-all-photos-title">Checklist photos ({activePhotoCount})</h4>
          <p className="sales-checklist-all-photos-hint">New uploads appear here and under the matching item above.</p>
          <ul className="sales-checklist-all-photos-grid">
            {attachmentsSorted.map((a) => {
              const src = attachmentApi.downloadUrl(a.id);
              const caption = attachmentCaption(a.section_id);
              const voided = isVoided(a);
              return (
                <li
                  key={a.id}
                  className={`sales-checklist-all-photos-item${voided ? ' attachment-voided-thumb' : ''}`}
                >
                  <a
                    href={src}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="sales-checklist-all-photos-link"
                    aria-label={`${caption}: ${a.original_file_name}`}
                  >
                    <img src={src} alt="" className="sales-checklist-all-photos-img" />
                  </a>
                  <span className="sales-checklist-all-photos-caption">{caption}</span>
                  {!disabled && (
                    <button
                      type="button"
                      className={voided ? 'attachment-unvoid-btn' : 'attachment-void-btn'}
                      disabled={photoVoid.busy}
                      onClick={() => (voided ? photoVoid.unvoid(a) : photoVoid.requestVoid(a))}
                    >
                      {voided ? 'Unvoid' : 'Void'}
                    </button>
                  )}
                  <AttachmentVoidReason attachment={a} />
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <AttachmentVoidModal control={photoVoid} />
    </section>
  );
};

export default InstallationChecklistCards;
